import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';

// ── icons ──────────────────────────────────────────────────────────────────────

function IconArrowLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/>
      <polyline points="12 19 5 12 12 5"/>
    </svg>
  );
}

function IconFolder() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

function IconFile() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
      <polyline points="13 2 13 9 20 9"/>
    </svg>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────────

function countWords(text) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

const DROPDOWN_STYLE = {
  position: 'absolute',
  top: 'calc(100% + 2px)',
  left: 0,
  right: 0,
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  zIndex: 200,
  maxHeight: 220,
  overflowY: 'auto',
};

const DROPDOWN_ITEM_BASE = {
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: 13,
  color: 'var(--text-primary)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  borderBottom: '1px solid var(--border)',
};

// ── RepoAutocomplete ───────────────────────────────────────────────────────────

function RepoAutocomplete({ value, onSelect, disabled, error }) {
  const [query, setQuery]           = useState(value ? value.repo_name : '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]             = useState(false);
  const [loading, setLoading]       = useState(false);
  const debounceRef                 = useRef(null);
  const wrapRef                     = useRef(null);

  // Keep display text in sync when parent clears selection
  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  const search = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get('/repositories', { params: { q, limit: 20 } });
        const items = res.data.data || [];
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {
        setSuggestions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  function handleChange(e) {
    const q = e.target.value;
    setQuery(q);
    onSelect(null); // clear selection until user picks
    search(q);
  }

  function handleSelect(repo) {
    setQuery(repo.repo_name);
    onSelect(repo);
    setSuggestions([]);
    setOpen(false);
  }

  function handleBlur(e) {
    if (wrapRef.current && wrapRef.current.contains(e.relatedTarget)) return;
    setTimeout(() => setOpen(false), 150);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} onBlur={handleBlur}>
      <input
        className={`form-input${error ? ' input-error' : ''}`}
        value={query}
        onChange={handleChange}
        onFocus={() => query && suggestions.length && setOpen(true)}
        placeholder="Type to search repositories…"
        disabled={disabled}
        autoComplete="off"
        style={error ? { borderColor: 'var(--danger)' } : {}}
      />
      {loading && (
        <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
          <div className="spinner" style={{ width: 12, height: 12 }} />
        </div>
      )}
      {open && suggestions.length > 0 && (
        <div style={DROPDOWN_STYLE}>
          {suggestions.map(repo => (
            <div
              key={repo.id}
              onMouseDown={() => handleSelect(repo)}
              style={DROPDOWN_ITEM_BASE}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-light)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <span style={{ fontFamily: 'monospace', flexGrow: 1, color: 'var(--text-primary)' }}>
                {repo.repo_name}
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>
                {repo.provider}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CodePathAutocomplete ───────────────────────────────────────────────────────

function CodePathAutocomplete({ repoId, value, onChange, disabled, error }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]               = useState(false);
  const [loading, setLoading]         = useState(false);
  const debounceRef                   = useRef(null);
  const wrapRef                       = useRef(null);

  const fetchTree = useCallback((repoId, dirPath) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`/repositories/${repoId}/tree`, {
          params: { path: dirPath },
        });
        const items = res.data.paths || [];
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {
        setSuggestions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, []);

  function handleChange(e) {
    const newVal = e.target.value;
    onChange(newVal);

    if (!repoId) { setSuggestions([]); setOpen(false); return; }

    const lastSlash = newVal.lastIndexOf('/');
    const dirPath   = lastSlash >= 0 ? newVal.substring(0, lastSlash) : '';
    const segment   = lastSlash >= 0 ? newVal.substring(lastSlash + 1) : newVal;

    // Don't try to autocomplete when the user is writing a glob suffix
    if (segment.includes('*') || segment.includes('?') || segment.includes('{')) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    // Re-use cached suggestions if the dirPath hasn't changed, just filter locally
    if (suggestions.length > 0 && lastSlash >= 0) {
      const filtered = segment
        ? suggestions.filter(p => p.name.toLowerCase().startsWith(segment.toLowerCase()))
        : suggestions;
      setSuggestions(filtered.length ? filtered : []);
      setOpen(filtered.length > 0);
      return;
    }

    fetchTree(repoId, dirPath);
  }

  function handleSelect(item) {
    const newPath = item.type === 'dir' ? item.path + '/' : item.path;
    onChange(newPath);
    // After selecting a directory, immediately fetch its contents
    if (item.type === 'dir') {
      fetchTree(repoId, item.path);
    } else {
      setSuggestions([]);
      setOpen(false);
    }
  }

  function handleBlur(e) {
    if (wrapRef.current && wrapRef.current.contains(e.relatedTarget)) return;
    setTimeout(() => setOpen(false), 150);
  }

  // Filter suggestions by the current segment for display
  const lastSlash = value.lastIndexOf('/');
  const segment   = lastSlash >= 0 ? value.substring(lastSlash + 1) : value;
  const displayed = segment && !segment.includes('*')
    ? suggestions.filter(p => p.name.toLowerCase().startsWith(segment.toLowerCase()))
    : suggestions;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} onBlur={handleBlur}>
      <input
        className={`form-input${error ? ' input-error' : ''}`}
        value={value}
        onChange={handleChange}
        onFocus={() => repoId && displayed.length && setOpen(true)}
        placeholder={repoId ? 'e.g. src/main/java/**/*.java' : 'Select a repository first'}
        disabled={disabled || !repoId}
        autoComplete="off"
        style={error ? { borderColor: 'var(--danger)' } : {}}
      />
      {loading && (
        <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
          <div className="spinner" style={{ width: 12, height: 12 }} />
        </div>
      )}
      {open && displayed.length > 0 && (
        <div style={DROPDOWN_STYLE}>
          {displayed.slice(0, 15).map((item, i) => (
            <div
              key={i}
              onMouseDown={() => handleSelect(item)}
              style={DROPDOWN_ITEM_BASE}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-light)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <span style={{ color: item.type === 'dir' ? 'var(--accent)' : 'var(--text-secondary)' }}>
                {item.type === 'dir' ? <IconFolder /> : <IconFile />}
              </span>
              <span style={{ fontFamily: 'monospace', flexGrow: 1, color: 'var(--text-primary)' }}>
                {item.path}
              </span>
              {item.type === 'dir' && (
                <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>dir</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Field error helper ─────────────────────────────────────────────────────────

function FieldError({ msg }) {
  if (!msg) return null;
  return <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{msg}</div>;
}

// ── EMPTY_FORM ─────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  product_id:    '',
  feature_id:    '',
  major_feature: '',
  sub_feature:   '',
  description:   '',
};

// ── FeatureForm ────────────────────────────────────────────────────────────────

export default function FeatureForm() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { id }    = useParams();
  const isEdit    = Boolean(id);

  const presetProductId = location.state?.product_id || '';

  const [form, setForm]               = useState({ ...EMPTY_FORM, product_id: String(presetProductId) });
  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(isEdit);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Feature-ID auto-generation + uniqueness check state
  const [fetchingNextId, setFetchingNextId] = useState(false);
  // 'idle' | 'checking' | 'available' | 'taken'
  const [idCheckState, setIdCheckState]     = useState('idle');
  const idCheckDebounce                     = useRef(null);

  // Code mapping (new feature only)
  const [codeRepo, setCodeRepo]   = useState(null);   // full repo object
  const [codePath, setCodePath]   = useState('');

  // Load products
  useEffect(() => {
    axios.get('/products', { params: { limit: 200 } })
      .then(res => setProducts(res.data.data || []))
      .catch(() => {});
  }, []);

  // Load existing feature for edit mode
  useEffect(() => {
    if (!isEdit) return;
    axios.get(`/features/${id}`)
      .then(res => {
        const d = res.data;
        setForm({
          feature_id:    d.feature_id || '',
          product_id:    d.product_id ? String(d.product_id) : '',
          major_feature: d.major_feature || '',
          sub_feature:   d.sub_feature || '',
          description:   d.description || '',
        });
      })
      .catch(() => setError('Failed to load feature'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // Auto-fetch next feature ID when product is selected (new feature only)
  useEffect(() => {
    if (isEdit || !form.product_id) return;
    setFetchingNextId(true);
    axios.get('/features/next-id', { params: { product_id: form.product_id } })
      .then(res => setForm(f => ({ ...f, feature_id: res.data.next_id || '' })))
      .catch(() => setForm(f => ({ ...f, feature_id: '' })))
      .finally(() => setFetchingNextId(false));
  }, [form.product_id, isEdit]);

  // Also auto-fetch if a product was preset on mount
  useEffect(() => {
    if (isEdit || !presetProductId) return;
    setFetchingNextId(true);
    axios.get('/features/next-id', { params: { product_id: presetProductId } })
      .then(res => setForm(f => ({ ...f, feature_id: res.data.next_id || '' })))
      .catch(() => {})
      .finally(() => setFetchingNextId(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live uniqueness check whenever feature_id changes (new feature only)
  useEffect(() => {
    if (isEdit || !form.feature_id.trim()) { setIdCheckState('idle'); return; }
    setIdCheckState('checking');
    if (idCheckDebounce.current) clearTimeout(idCheckDebounce.current);
    idCheckDebounce.current = setTimeout(() => {
      axios.get('/features/check-id', { params: { feature_id: form.feature_id.trim() } })
        .then(res => setIdCheckState(res.data.available ? 'available' : 'taken'))
        .catch(() => setIdCheckState('idle'));
    }, 500);
    return () => clearTimeout(idCheckDebounce.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.feature_id, isEdit]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (fieldErrors[name]) setFieldErrors(fe => ({ ...fe, [name]: '' }));
  }

  function validate() {
    const errs = {};
    if (!form.product_id)           errs.product_id    = 'Product is required';
    if (!form.feature_id.trim())    errs.feature_id    = 'Feature ID is required';
    else if (idCheckState === 'taken') errs.feature_id = 'This Feature ID is already taken';
    else if (idCheckState === 'checking') errs.feature_id = 'Still checking uniqueness — please wait';
    if (!form.major_feature.trim()) errs.major_feature = 'Major Feature is required';
    if (!form.sub_feature.trim())   errs.sub_feature   = 'Sub Feature is required';
    if (!form.description.trim()) {
      errs.description = 'Description is required';
    } else {
      const wc = countWords(form.description);
      if (wc < 30) errs.description = `Description must be at least 30 words (currently ${wc})`;
    }
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        feature_id:    form.feature_id.trim(),
        product_id:    form.product_id ? Number(form.product_id) : null,
        major_feature: form.major_feature.trim(),
        sub_feature:   form.sub_feature.trim() || null,
        description:   form.description.trim() || null,
      };

      if (isEdit) {
        await axios.put(`/features/${id}`, payload);
        // Code mapping is managed from the detail page in edit mode
        navigate(`/features/${id}`);
      } else {
        const res = await axios.post('/features', payload);
        const newId = res.data.id;

        // Add inline code mapping if provided
        if (codeRepo && codePath.trim()) {
          const repoSlug = codeRepo.repo_name.split('/').pop();
          try {
            await axios.post(`/features/${newId}/code-files`, {
              repo_name:    repoSlug,
              glob_pattern: codePath.trim(),
            });
          } catch {
            // Non-fatal: feature is created, mapping failed silently
          }
        }

        navigate(`/features/${newId}`);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <span>Loading feature…</span>
      </div>
    );
  }

  const wordCount = countWords(form.description);

  return (
    <div>
      <div className="page-header">
        <h2>{isEdit ? 'Edit Feature' : 'New Feature'}</h2>
        <p>{isEdit ? `Editing ${form.feature_id}` : 'Fill in the fields below to create a new product feature'}</p>
      </div>

      <div className="page-body" style={{ maxWidth: 720 }}>
        <button
          className="btn btn-ghost"
          style={{ marginBottom: 20, paddingLeft: 0 }}
          onClick={() => navigate(isEdit ? `/features/${id}` : '/features')}
        >
          <IconArrowLeft /> Back
        </button>

        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          {/* ── Product + Feature ID card ─────────────────────────────────── */}
          <div className="card" style={{ marginBottom: 16 }}>
            {/* Product – first and mandatory */}
            <div className="form-group">
              <label className="form-label" htmlFor="product_id">
                Product <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <select
                id="product_id"
                name="product_id"
                className="form-input"
                value={form.product_id}
                onChange={handleChange}
                disabled={isEdit}
                style={{
                  ...(isEdit ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                  ...(fieldErrors.product_id ? { borderColor: 'var(--danger)' } : {}),
                }}
              >
                <option value="">— Select a product —</option>
                {products.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.name}</option>
                ))}
              </select>
              <FieldError msg={fieldErrors.product_id} />
            </div>

            {/* Feature ID – auto-generated then editable on new; locked on edit */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="feature_id">
                Feature ID
                {!isEdit && (
                  <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                    (auto-generated, editable)
                  </span>
                )}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="feature_id"
                  name="feature_id"
                  className="form-input"
                  value={fetchingNextId ? '' : form.feature_id}
                  onChange={isEdit ? undefined : handleChange}
                  readOnly={isEdit}
                  disabled={isEdit || (!form.product_id && !form.feature_id)}
                  placeholder={
                    fetchingNextId
                      ? 'Generating…'
                      : form.product_id
                        ? 'e.g. VOICE-FEAT-023'
                        : 'Select a product first'
                  }
                  style={{
                    paddingRight: 36,
                    ...(isEdit ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                    ...(fieldErrors.feature_id ? { borderColor: 'var(--danger)' } : {}),
                    ...(idCheckState === 'available' && !fieldErrors.feature_id
                        ? { borderColor: 'var(--success)' } : {}),
                  }}
                />
                {/* Right-side status indicator */}
                <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                  {fetchingNextId && <div className="spinner" style={{ width: 14, height: 14 }} />}
                  {!fetchingNextId && idCheckState === 'checking' && (
                    <div className="spinner" style={{ width: 14, height: 14 }} />
                  )}
                  {!fetchingNextId && idCheckState === 'available' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                  {!fetchingNextId && idCheckState === 'taken' && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  )}
                </div>
              </div>
              <FieldError msg={fieldErrors.feature_id} />
              {!isEdit && !fieldErrors.feature_id && idCheckState === 'available' && (
                <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 4 }}>
                  ID is available
                </div>
              )}
              {!isEdit && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Auto-filled from existing pattern — edit freely. Cannot be changed after creation.
                </div>
              )}
            </div>
          </div>

          {/* ── Feature details card ──────────────────────────────────────── */}
          <div className="card" style={{ marginBottom: 16 }}>
            {/* Major Feature */}
            <div className="form-group">
              <label className="form-label" htmlFor="major_feature">
                Major Feature <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                id="major_feature"
                name="major_feature"
                className="form-input"
                placeholder="High-level feature name"
                value={form.major_feature}
                onChange={handleChange}
                style={fieldErrors.major_feature ? { borderColor: 'var(--danger)' } : {}}
              />
              <FieldError msg={fieldErrors.major_feature} />
            </div>

            {/* Sub Feature */}
            <div className="form-group">
              <label className="form-label" htmlFor="sub_feature">
                Sub Feature <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                id="sub_feature"
                name="sub_feature"
                className="form-input"
                placeholder="Lower-level feature or component"
                value={form.sub_feature}
                onChange={handleChange}
                style={fieldErrors.sub_feature ? { borderColor: 'var(--danger)' } : {}}
              />
              <FieldError msg={fieldErrors.sub_feature} />
            </div>

            {/* Description */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="description">
                Description <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <textarea
                id="description"
                name="description"
                className="form-input"
                placeholder="Describe what this feature does and how it works… (minimum 30 words)"
                value={form.description}
                onChange={handleChange}
                rows={6}
                style={{
                  resize: 'vertical',
                  lineHeight: 1.6,
                  ...(fieldErrors.description ? { borderColor: 'var(--danger)' } : {}),
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <FieldError msg={fieldErrors.description} />
                <span style={{
                  fontSize: 11,
                  color: wordCount >= 30 ? 'var(--success, #16a34a)' : 'var(--text-muted)',
                  marginLeft: 'auto',
                }}>
                  {wordCount} / 30 words
                </span>
              </div>
            </div>
          </div>

          {/* ── Code mapping card (new features only) ────────────────────── */}
          {!isEdit && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Code Mapping</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Optional — link this feature to a specific code path in a repository.
                  More mappings can be added from the feature detail page.
                </div>
              </div>

              {/* Repository autocomplete */}
              <div className="form-group">
                <label className="form-label">Repository</label>
                <RepoAutocomplete
                  value={codeRepo}
                  onSelect={repo => {
                    setCodeRepo(repo);
                    setCodePath('');
                  }}
                  error={null}
                />
                {codeRepo && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Selected: <strong>{codeRepo.repo_name}</strong> ({codeRepo.provider})
                    <button
                      type="button"
                      onClick={() => { setCodeRepo(null); setCodePath(''); }}
                      style={{
                        marginLeft: 8, background: 'none', border: 'none',
                        color: 'var(--danger)', cursor: 'pointer', fontSize: 11,
                      }}
                    >
                      clear
                    </button>
                  </div>
                )}
              </div>

              {/* Code path autocomplete */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Code Path</label>
                <CodePathAutocomplete
                  repoId={codeRepo?.id || null}
                  value={codePath}
                  onChange={setCodePath}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Path or glob pattern in the repo, e.g. <code>src/voice/**/*.java</code>.
                  Type a directory prefix for autocomplete from the repo's default branch.
                </div>
              </div>
            </div>
          )}

          {/* ── Actions ───────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(isEdit ? `/features/${id}` : '/features')}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</>
                : isEdit ? 'Save Changes' : 'Create Feature'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
